import type { MarkerPosition } from "@/models/marker-position";
import { schema as markerSchema, toRaw } from "@/models/marker-position";
import { schema as collection } from "@/models/passage-collection";
import { Api } from "@/utils";

const baseUrl = "v1/marker-positions";

/**
 * Fetches marker positions for a floor space and parses them with Zod.
 *
 * @param {number} floorSpaceId - The floor space ID to fetch markers for.
 * @returns {Promise<MarkerPosition[]>} The parsed marker positions.
 */
export const getByFloorSpaceId = async (
    floorSpaceId: number,
): Promise<MarkerPosition[]> => {
    const response = await Api.get(`${baseUrl}/floor-space-id/${floorSpaceId}`);
    const { result } = collection(markerSchema).parse(response);
    return result;
};

/**
 * Saves a list of marker positions to the API.
 *
 * @param {MarkerPosition[]} markers - The marker positions to save.
 * @returns {Promise<MarkerPosition[]>} The saved marker positions.
 */
export const save = async (
    markers: MarkerPosition[],
): Promise<MarkerPosition[]> => {
    const raw = markers.map(toRaw);
    const response = await Api.put(`${baseUrl}/`, raw);
    const { result } = collection(markerSchema).parse(response);
    return result;
};

/**
 * Deletes marker positions by IDs.
 *
 * @param {number[]} ids - The marker position IDs to delete.
 */
export const deleteMany = async (ids: number[]): Promise<void> => {
    await Api.delete(`${baseUrl}`, {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markerPositionIds: ids }),
    });
};
